import Sticker, { StickerIcon } from '@@types/Sticker';

import { EMOJI_CATEGORY } from '~/components/UiKit/emoji-selector/EmojiSelector.constant';
import { Emoticon, EmoticonColCount } from '~/constants';
import { computed } from 'mobx';

interface DetailState {
  readonly stickers: Sticker[];

  readonly tab: string;

  readonly activeIndex: number;
  setActiveIndex(index: number): void;
}

abstract class Detail {
  protected abstract colCount: number;
  protected constructor(protected state: DetailState) {}

  abstract enter(): void;

  protected abstract get size(): number;

  canMoveLeftOnly = () => {
    return this.canMoveHorizontal(-1);
  };

  canMoveRightOnly = () => {
    return this.canMoveHorizontal(1);
  };

  left = () => {
    if (this.canMoveLeftOnly()) {
      this.moveHorizontal(-1);
    }
  };

  right = () => {
    if (this.canMoveRightOnly()) {
      this.moveHorizontal(1);
    }
  };

  onChangeTabLeft = () => {
    const index = this.getFirstIndexInRow(this.state.activeIndex) + this.colCount - 1;
    this.onChangeTab(index);
  };

  onChangeTabRight = () => {
    const index = this.getFirstIndexInRow(this.state.activeIndex);
    this.onChangeTab(index);
  };

  up = () => {
    if (this.state.activeIndex < this.colCount) {
      this.state.setActiveIndex(-1);
      return;
    }
    this.moveVertical(-1, this.size);
  };

  down = () => {
    this.moveVertical(1, this.size);
  };

  public shiftTab = () => {
    this.state.setActiveIndex(-1);
  };

  private getFirstIndexInRow = (index: number) => {
    return index - (index % this.colCount);
  };

  private canMoveHorizontal = (delta: number) => {
    const newIndex = this.state.activeIndex + delta;
    const prevRow = Math.floor(this.state.activeIndex / this.colCount);
    const newRow = Math.floor(newIndex / this.colCount);
    return prevRow === newRow && this.canMoveIndex(newIndex);
  };

  private onChangeTab = (index: number) => {
    if (this.canMoveIndex(index)) {
      this.state.setActiveIndex(index);
    } else {
      this.state.setActiveIndex(this.size - 1);
    }
  };

  private canMoveIndex = (index: number) => {
    return index > -1 && index < this.size;
  };

  protected moveHorizontal = (delta: number) => {
    const newIndex = this.state.activeIndex + delta;
    this.state.setActiveIndex(newIndex);
  };

  protected moveVertical(delta, size) {
    const newIndex = this.state.activeIndex + delta * this.colCount;
    if (newIndex > -1 && newIndex < size) {
      this.state.setActiveIndex(newIndex);
    }
  }
}

export class StickerDetail extends Detail {
  protected colCount = EmoticonColCount[Emoticon.Sticker];

  constructor(state: DetailState, private select: (pack: Sticker, sticker: StickerIcon) => void) {
    super(state);
  }

  @computed
  private get pack() {
    return this.state.stickers?.find((sticker) => sticker.id === this.state.tab);
  }

  @computed
  protected get size() {
    return this.pack?.stickers.length;
  }

  @computed
  protected get sticker() {
    return this.pack?.stickers[this.state.activeIndex];
  }

  enter = () => {
    this.select(this.pack, this.sticker);
  };
}

export class EmoticonDetail extends Detail {
  protected colCount = EmoticonColCount[Emoticon.Emoticon];

  constructor(state: DetailState, private select: (emoji: string) => void) {
    super(state);
  }

  protected get itemList() {
    return EMOJI_CATEGORY[this.state.tab];
  }

  protected get size() {
    return this.itemList.length;
  }

  enter = () => {
    this.select(this.itemList[this.state.activeIndex]);
  };
}
